import React, { useState } from 'react';
import {
  Box,
  Collapse,
  IconButton,
  InputAdornment,
  MenuItem,
  Popover,
  Select,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import CheckIcon from '@mui/icons-material/Check';
import { LocalizationProvider, DesktopDatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useNavigate } from 'react-router-dom';
import { useProgramContext } from '../../../../../../hooks/useProgramContext';
import useOrgLevelAdmin from '../../../../../../hooks/useOrgLevelAdmin';
import useProgramCreationFlowV2 from '../../../../../../hooks/useProgramCreationFlowV2';
import { AIMD_STEPS } from '../../../aimd-v2/types';
import { roleLockLabels, useRoleLock } from '../../../capability-setup/create/roleLock';
import { snapshotIn } from '../../../capability-setup/create/trainingSnapshot';
import { API } from '../../../../../../api';
import ROLES from '../../../../../../utils/roles';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ProgramNameText from '../../../../../../components/ui/ProgramNameText';
import ProgramKindIcon from '../../../../../../components/ui/ProgramKindIcon';
import type { AssessmentCardInfo } from './adminAssessmentCards';
import { MOBILE_SX, useIsMobileApp } from '../../../../../../mobile/mobileApp';
import { PROGRAM_TYPE_COLORS } from './constants';
import type { ProgramCardData, ProgramDueDate, ProgramOwner, ProgramStatus, ProgramTypeKey } from './types';
import { existingLearnersDueBy, isProgramDueUrgent } from './programDueDate';
import { resolveAudienceLabels, useOrgAudienceOptions } from './useOrgMemberAttributes';
import {
  audienceRulesFromFlat,
  flattenAudienceRules,
  participatingAudienceRules,
  type AudienceMatchType,
  type AudienceRule,
  type MemberAttributeGroupCatalog
} from '../../../../utils/memberAttributes';
import DosenButton from '../../../../../../components/ui/Buttons/DosenButton';
import { formatDate, fromEpochSeconds } from '../../../../../../utils/formatDate';

// Progress bar fill colour — tiered by completion rate per Cian's spec:
//   ≥ 70%  green   #00972D (UI/Success)
//   30–69% amber   #F0B80D
//   < 30%  orange  #F79420 (Brandy orange-500)
const PROGRESS_TRACK_BG = '#2A2A2A';
const getProgressFill = (rate: number): string => {
  if (rate >= 70) return '#00972D';
  if (rate >= 30) return '#F0B80D';
  return '#F79420';
};

// Amber used for "Not Started" status text — same hex as the 30–69% bar tier.
const STATUS_NOT_STARTED_COLOR = '#F0B80D';

// Red for anything overdue or close to it — the Launch By flag, the urgent
// "!" and the urgent card outline. Same hex as the learner card's.
const URGENT_RED = '#DA2828';

/**
 * Completion dial — the card's headline figure when collapsed. Same colour
 * tiers as the expanded progress bar, so a card reads the same either way.
 */
const CompletionDial: React.FC<{ rate: number; size?: number; tooltip?: string }> = ({
  rate,
  size = 68,
  tooltip = PROGRAM_COMPLETION_TOOLTIP
}) => {
  const clamped = Math.min(100, Math.max(0, Math.round(rate)));
  // A small dial (the phone's compact card) takes a thinner ring and a smaller figure.
  const stroke = size >= 80 ? 7 : size < 56 ? 4 : 6;
  const r = (size - stroke) / 2;
  const circumference = 2 * Math.PI * r;
  return (
    <Tooltip title={tooltip}>
      <Box sx={{ position: 'relative', width: size, height: size, flexShrink: 0, cursor: 'default' }}>
        <Box component="svg" width={size} height={size} sx={{ display: 'block', transform: 'rotate(-90deg)' }}>
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={PROGRESS_TRACK_BG} strokeWidth={stroke} />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke={getProgressFill(clamped)}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={circumference * (1 - clamped / 100)}
            style={{ transition: 'stroke-dashoffset 0.3s' }}
          />
        </Box>
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontSize: size >= 80 ? 18 : size < 56 ? 11.5 : 16,
              fontWeight: 700,
              color: '#FDFDFD',
              lineHeight: 1
            }}
          >
            {clamped}%
          </Typography>
        </Box>
      </Box>
    </Tooltip>
  );
};

const PROGRAM_COMPLETION_TOOLTIP =
  'Percentage of program members who have completed this program. Calculated across active and invited members.';

// A program is "live" once learners can see it (invited or active). Before that
// it is still being built, so a completion dial would only ever read 0% — the
// tile shows the build pipeline instead.
const isProgramLive = (status: ProgramStatus): boolean => status === 'invited' || status === 'active';

// The build-to-launch pipeline, in order. A not-yet-live program sits somewhere
// on this line and the tile lights the stage it is currently at:
//   design / not-started → Design (resume the builder)
//   published            → Design + Publish done, awaiting Invite — the action
//                          that moves a program to Invited status
const BUILD_STAGES = ['Design', 'Publish', 'Invite'] as const;
// What each dot reads on the card. BUILD_STAGES stays as it is because the
// hover text names the stage with it.
const BUILD_STAGE_LABELS = ['Design', 'Publish', 'Enroll'] as const;
const currentBuildStage = (status: ProgramStatus): number => (status === 'published' ? 2 : 0);

/**
 * Build pipeline shown on a not-yet-live tile in place of the completion dial.
 * A 0% dial on a draft was noise ("airplane dashboard"); this says where the
 * program is and what is left to get it live. Completed stages carry a green
 * tick, the current stage a lit brandy node, the rest sit hollow.
 */
const StageStepper: React.FC<{ status: ProgramStatus; note?: string; compact?: boolean }> = ({
  status,
  note,
  compact = false
}) => {
  const current = currentBuildStage(status);
  // Compact (the phone's one-row card): smaller nodes on a fixed width, the labels kept readable.
  const node = compact ? 16 : 21;
  return (
    <Tooltip
      title={
        note ?? `This program is at the ${BUILD_STAGES[current]} stage. Continue to move it towards inviting members.`
      }
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          width: compact ? 120 : '100%',
          px: compact ? 0 : '8px',
          cursor: 'default'
        }}
      >
        {BUILD_STAGE_LABELS.map((label, i) => {
          const done = i < current;
          const cur = i === current;
          const last = i === BUILD_STAGE_LABELS.length - 1;
          return (
            <Box
              key={label}
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: compact ? '4px' : '6px',
                position: 'relative'
              }}
            >
              {/* Track to the next node — green once this stage is behind us. */}
              {!last && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: compact ? `${node / 2 - 1}px` : '10px',
                    left: '50%',
                    right: '-50%',
                    height: '2px',
                    bgcolor: done ? '#00972D' : '#333333',
                    zIndex: 0
                  }}
                />
              )}
              <Box
                sx={{
                  width: node,
                  height: node,
                  borderRadius: '9999px',
                  zIndex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: compact ? 9.5 : 11,
                  fontWeight: 700,
                  lineHeight: 1,
                  border: `1.5px solid ${done ? '#00972D' : cur ? '#F79420' : '#444444'}`,
                  bgcolor: done ? '#00972D' : cur ? '#F79420' : '#1E1E1E',
                  color: done ? '#FDFDFD' : cur ? '#FDFDFD' : '#565656',
                  boxShadow: cur ? '0 0 0 3px rgba(247,148,32,0.14)' : 'none'
                }}
              >
                {done ? '✓' : i + 1}
              </Box>
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: compact ? 10 : 10.5,
                  lineHeight: 1,
                  color: done ? '#B6B6B6' : cur ? '#F79420' : '#868686',
                  fontWeight: cur ? 600 : 400
                }}
              >
                {label}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Tooltip>
  );
};

// Admin-facing lock copy — says what the lock does to learners, since the
// admin's own access to the program is unaffected.
const PROGRAM_LOCKED_TOOLTIP =
  "Locked — learners can see this program's name on their Dashboard but can't access it or see any lessons until you unlock it from the padlock in the Program Designer.";

interface Props {
  program: ProgramCardData;
  // Org members eligible as program owners. Fed from useAdminLearningLifecycle's
  // orgMembers (normalised from GET /getUsersByOrg/{orgId}).
  existingMembers?: ProgramOwner[];
  // Inline edit handlers — fired when the admin saves one of the
  // popover editors anchored to the +Add CTAs. Optional so this card
  // is still safe to render in a read-only context.
  // Brandy outline around the whole card (freshly-created program highlight).
  highlighted?: boolean;
  // Run Continue once on mount — a co-design invite (bell / email / Teams)
  // opens straight into this program's builder.
  autoContinue?: boolean;
  // The viewer can't change this program: shown without editors or Continue.
  readOnly?: boolean;
  // Capability release: an Assessment, shown as a card beside the Trainings
  // (see adminAssessmentCards.ts). It moves along the same Design, Publish,
  // Invite line, then shows the dial: how many of its people have completed it.
  // Nothing on it is edited from the card: Continue reopens Run Assessment
  // while it is being built, and after that opens what it asks, read-only.
  // No `onOpen`, no Continue: one being built that the viewer may not change.
  assessment?: AssessmentCardInfo & { onOpen?: () => void };
  // Fires when the details expander is toggled, so the column can pin a
  // collapsed card to a fixed height and let an expanded one grow.
  onDetailsToggle?: (open: boolean) => void;
  onAudienceChange?: (rules: AudienceRule[] | null) => void;
  // Dry-run of a pending audience change, used to gate the save behind an
  // impact confirmation. Optional — without it the popover saves directly.
  onAudiencePreview?: (rules: AudienceRule[]) => Promise<{ toAdd: number; toRemove: number } | null>;
  onLaunchDateChange?: (launchDate: number | null) => void;
  onOwnerChange?: (owner: ProgramOwner | null) => void;
  // Fires when the admin picks a new category from the colored dot.
  onProgramTypeChange?: (programType: ProgramTypeKey) => void;
  // Shared "Continue" navigation lock across all cards in the dashboard.
  // `continuingId` is the id of the card currently navigating (null = none);
  // onContinueStart flips that shared flag so every other card's Continue
  // ignores clicks while one is in flight (without changing how it looks).
  // No "end" handler is needed: every Continue path navigates away, which
  // unmounts the dashboard and discards the lock.
  continuingId?: string | null;
  onContinueStart?: (id: string) => void;
  // Creation-flow revamp §1 Case A: Continue on an AI build that has NOT yet
  // reached Structure/Content resumes into the Add Program modal's embedded
  // wizard instead of the standalone page. The dashboard owns the modal.
  onResumeInDrawer?: (groupId: string, programName: string) => void;
  // Creation-flow revamp: Continue on a program published but not yet Invite &
  // Finished reopens its Members step in the modal, whichever flow built it.
  onOpenMembers?: (groupId: string, programName: string) => void;
  // Capability release: a program built in the Publish Training flow reopens
  // there, at the step it was left (Details through Members). Absent where
  // that flow is not offered, and such a program then resumes as any AI build.
  // `published`: its content is out, so only Members is left.
  onResumeTraining?: (groupId: string, published: boolean) => void;
  // Continue on a program with lessons opens its editor in the Program Editor
  // modal over the board, instead of the /designer page. Absent, it navigates.
  onOpenEditor?: (program: ProgramCardData) => void;
}

// "design" → "Design", "not-started" → "Not Started"
const capitalizeWords = (raw: string): string =>
  raw
    .split(/[-\s]/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

// Display label for a program model. Internal model ids stay unchanged;
// only the user-facing label is remapped ("masterclass" → "Course").
const MODEL_DISPLAY_LABELS: Record<string, string> = {
  masterclass: 'Course',
  'guided-masterclass': 'Guided Course',
  mentorship: 'Mentorship'
};
export const formatModelLabel = (raw: string): string => MODEL_DISPLAY_LABELS[raw] || capitalizeWords(raw);

// On-screen status, sentence case ("Not started"). An invited program reads
// Enrolled, as the board's column does. The stored status is unchanged.
const formatStatusLabel = (raw: string): string => {
  if (raw === 'invited') return 'Enrolled';
  const words = raw.split(/[-\s]/).join(' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
};

// The card's on-screen dates: "Jun 30, 2026".
export const formatCardDate = (unix: number): string => formatDate(fromEpochSeconds(unix));

// 1782777600 (unix s) → "June 30th"
const formatLaunchDate = (unix: number): string => {
  const d = new Date(unix * 1000);
  const month = d.toLocaleString('en-US', { month: 'long' });
  const day = d.getDate();
  const mod100 = day % 100;
  const mod10 = day % 10;
  const suffix =
    mod100 >= 11 && mod100 <= 13 ? 'th' : mod10 === 1 ? 'st' : mod10 === 2 ? 'nd' : mod10 === 3 ? 'rd' : 'th';
  return `${month} ${day}${suffix}`;
};

/**
 * Single program card inside a Learning Pillar column.
 *
 * Built in stages — this scaffold currently renders only the outer card
 * wrapper and the name pill (per Figma spec). Subsequent fields (Model,
 * Status, Audience, Launch By, progress bar, Owner, Continue) will be
 * added one at a time as Cian confirms each spec.
 *
 * Figma:
 *   Wrapper: bg #151515 (Fill/Card/card-default), border 1px #2A2A2A
 *            (Stroke/Form/Input/border-dim), padding 15 all sides
 *   Name pill: bg #2A2A2A (Fill/tooltip-dim), border 1px #444444
 *              (Stroke/Border/border), radius 4, padding 6/8, gap 6,
 *              width fit-content (sizes to program-name length)
 *   Type dot: 8x8 round, bg per programType
 *   Title: Inter 15/600, #FDFDFD
 */
const ProgramCard: React.FC<Props> = ({
  program,
  existingMembers = [],
  onAudienceChange,
  onAudiencePreview,
  onLaunchDateChange,
  onOwnerChange,
  onProgramTypeChange,
  continuingId = null,
  onContinueStart,
  onResumeInDrawer,
  onOpenMembers,
  onResumeTraining,
  onOpenEditor,
  highlighted = false,
  autoContinue = false,
  readOnly = false,
  assessment,
  onDetailsToggle
}) => {
  const typeColor = PROGRAM_TYPE_COLORS[program.programType];
  // Drives the Continue CTA and whether the collapsed tile shows the
  // completion dial (live) or the build pipeline (not yet live).
  const isLive = isProgramLive(program.status);
  const isMobileApp = useIsMobileApp();
  const navigate = useNavigate();
  const { orgId, coId } = useProgramContext();
  const orgLevelAdmin = useOrgLevelAdmin();
  const creationFlowV2 = useProgramCreationFlowV2();
  // Resolve audience value-ids → labels via the org's memberAttributes
  // (sourced from getOrgName). The picker writes per-list rules back to the BE.
  const { labelById: audienceLabelById, catalog: audienceCatalog } = useOrgAudienceOptions();
  // Programs saved before the audienceRules migration only have the flat
  // `audience` list — synthesise "any" rules per owning list so the popover
  // opens pre-filled instead of blank.
  const audienceRules = React.useMemo<AudienceRule[]>(
    () =>
      program.audienceRules && program.audienceRules.length > 0
        ? program.audienceRules
        : audienceRulesFromFlat(program.audience, audienceCatalog),
    [program.audienceRules, program.audience, audienceCatalog]
  );
  // A program built for roles in Publish Training (any category) is for its roles only: they are its
  // audience, and neither they nor its type are changed from the card (see roleLock.ts).
  const roleLock = useRoleLock(program.roleIds);
  const audienceLabels = roleLock
    ? roleLockLabels(roleLock)
    : resolveAudienceLabels(flattenAudienceRules(audienceRules), audienceLabelById);

  // ProgramCard is only mounted inside AdminLearningLifecycleDashboard,
  // which the upstream ProgramTable already gates to admin roles. Anyone
  // rendering this card is authorised — the BE PATCH does the final auth
  // anyway. Keep the prop wiring simple and always show the "Add Owner"
  // CTA when there is no owner.
  // Read-only for a viewer who can't change this program (a team manager
  // seeing a program their team takes): no editors, no Continue.
  const editable = !readOnly;
  // A program for roles takes no other audience, and keeps its category.
  const audienceEditable = editable && !roleLock;
  const typeEditable = editable && !roleLock;
  // The owner becomes a program admin, so the BE only lets an org admin set
  // it: group admins and team managers (Member role) see the owner read-only.
  const viewerRole = localStorage.getItem('role') || '';
  const canEditOwner = editable && viewerRole !== ROLES.grpAdmin && viewerRole !== ROLES.member;

  // Launch By 14-day red-flag (PRD §5). Conditions:
  //   • program has a launchDate set
  //   • status is Design or Published (not yet Invited / Active)
  //   • launchDate is within 14 days of now (including past-due)
  // Visual only — no notification (per Cian's earlier confirmation on Q6).
  const launchByValueColor = (() => {
    if (program.launchDate == null) return undefined;
    const pre = program.status === 'design' || program.status === 'not-started' || program.status === 'published';
    if (!pre) return undefined;
    const nowSeconds = Math.floor(Date.now() / 1000);
    const diffDays = (program.launchDate - nowSeconds) / 86400;
    return diffDays <= 14 ? URGENT_RED : undefined;
  })();

  // Existing learners are due within two weeks (or overdue) and not everyone
  // has finished — the red "!" and a red outline, as on the learner's card.
  const nowSeconds = Math.floor(Date.now() / 1000);
  const urgent = isProgramDueUrgent(program, nowSeconds);
  const existingDueBy = existingLearnersDueBy(program.programDueDate);
  const urgentTooltip =
    urgent && existingDueBy !== null
      ? `Existing learners ${existingDueBy < nowSeconds ? 'were' : 'are'} due ${formatLaunchDate(
          existingDueBy
        )} and this program is ${Math.round(program.programCompletionRate)}% complete.`
      : '';

  // Anchor refs for the three inline popover editors. Only one open at a
  // time in practice — each is its own popover so they can be independent.
  const [audienceAnchor, setAudienceAnchor] = useState<HTMLElement | null>(null);
  const [launchByAnchor, setLaunchByAnchor] = useState<HTMLElement | null>(null);
  const [ownerAnchor, setOwnerAnchor] = useState<HTMLElement | null>(null);
  const [typeAnchor, setTypeAnchor] = useState<HTMLElement | null>(null);
  // Collapsed by default — name, completion dial and owner only. The dashboard
  // needs to know: a collapsed card is pinned to an exact height so it lines up
  // with the Recommended Course cards, and only grows once expanded.
  const [detailsOpen, setDetailsOpen] = useState(false);
  const toggleDetails = () =>
    setDetailsOpen((open) => {
      onDetailsToggle?.(!open);
      return !open;
    });

  // Continue navigation:
  //   • design / not-started → resume the builder where the admin left off.
  //     We read the saved buildState to jump straight to the right surface:
  //       - AI courses (aimd-v2 buildState) → /aimd-v2 with a resume flag, so
  //         the wizard reopens on the saved step without the type-pick,
  //         mode-pick and "Resume Program?" prompt.
  //       - everything else (manual / mentorship / fresh stub) → the creation
  //         funnel at /new-program/start, where ProgramCreationFlow resumes
  //         to the matching step from the same buildState.
  //   • published masterclass / guided → /settings?section=settings
  //   • published mentorship / other   → /settings?section=members
  // While any card is navigating, every other card ignores clicks (double-click
  // guard) but looks unchanged; only the clicked card reads "Opening...".
  const isContinuing = continuingId === program.id;
  const anyContinuing = continuingId !== null;

  // Course / Guided Course / Hybrid: the editor, in the modal when the board offers it.
  const openDesigner = (base: string) => {
    if (onOpenEditor) onOpenEditor(program);
    else navigate(`${base}/designer`);
  };

  const handleContinue = async () => {
    if (anyContinuing) return;
    // Creation-flow revamp: Published means content is out but nobody has
    // clicked Invite & Finish — straight to the Members step, for every build type.
    if (creationFlowV2 && program.status === 'published' && onOpenMembers) {
      // Built in Publish Training: Members is the last of that flow's steps.
      if (onResumeTraining) {
        onContinueStart?.(program.id);
        const saved = await API.getBuildState({ grpId: program.id }).catch(() => null);
        if (snapshotIn(saved?.data)) {
          onResumeTraining(program.id, true);
          return;
        }
      }
      onOpenMembers(program.id, program.name);
      return;
    }
    onContinueStart?.(program.id);
    const isDesignStage = program.status === 'design' || program.status === 'not-started';
    const isMasterclass = program.model === 'masterclass' || program.model === 'guided-masterclass';
    const base = `/app/programdetails/${orgId}/${coId}/${program.id}`;

    // Creation-flow revamp §1: a stage-6 publish moves status to 'published'
    // WITHOUT anyone invited — such a program is still mid-creation (Members
    // pending), so Continue must resume the wizard, not land on /designer.
    // Legacy (flag off) keeps the old design-only gate.
    const isResumableStage = creationFlowV2
      ? program.status !== 'invited' && program.status !== 'active'
      : isDesignStage;

    if (isResumableStage) {
      let buildState: any = null;
      try {
        const resp = await API.getBuildState({ grpId: program.id });
        buildState = resp?.data ?? null;
      } catch {
        // No saved state reachable — fall through to the funnel start.
      }
      // AI masterclass/guided builds resume in the /aimd-v2 wizard, which skips
      // the type-pick, mode-pick AND "Resume Program?" prompt entirely. Detect
      // an AI build from EITHER:
      //   • a clean aimd-v2 buildState — wizard markers (requirements/outcomes/
      //     ai/highestCompletedStep) and no legacy `programType`; or
      //   • the legacy funnel stub that recorded an AI build (`buildMode:'ai'`)
      //     on a masterclass/guided program — what's left on the group when an
      //     earlier aimd-v2 BE save didn't land (real progress sits in
      //     localStorage, which the /aimd-v2 resume reconciles).
      // Manual masterclass/guided and mentorship fall through to the funnel,
      // whose resume effect jumps straight to the saved step.
      const bs = buildState && typeof buildState === 'object' ? buildState : null;
      if (onResumeTraining && snapshotIn(bs)) {
        onResumeTraining(program.id, false);
        return;
      }
      const isMasterclassOrGuided = program.model === 'masterclass' || program.model === 'guided-masterclass';
      const hasAimdMarkers =
        !!bs &&
        (bs.requirements !== undefined ||
          bs.outcomes !== undefined ||
          bs.ai !== undefined ||
          typeof bs.highestCompletedStep === 'number');
      const isAiCourse =
        (hasAimdMarkers && bs.programType === undefined) || (!!bs && bs.buildMode === 'ai' && isMasterclassOrGuided);

      if (isAiCourse) {
        // §1 split (flag on): Case A — the build hasn't reached Structure yet
        // (no generated artifacts, nothing past Preview completed, and not yet
        // published) → resume inside the Add Program modal's embedded wizard.
        // Case B — Structure/Content/Members reached → the standalone page,
        // whose own resume math lands on the correct step (incl. Members).
        const structureIdx = AIMD_STEPS.indexOf('structure');
        const reachedStructure =
          program.status === 'published' ||
          !!bs?.ai?.structure ||
          !!bs?.ai?.content ||
          (typeof bs?.highestCompletedStep === 'number' && bs.highestCompletedStep >= structureIdx - 1);
        if (creationFlowV2 && !reachedStructure && onResumeInDrawer) {
          onResumeInDrawer(program.id, program.name);
          return;
        }
        navigate(`${base}/aimd-v2`, { state: { resume: true } });
      } else if (isDesignStage) {
        navigate(`${base}/new-program/start`);
      } else if (orgLevelAdmin) {
        // Widened gate caught a published non-AI program (manual/mentorship —
        // out of the revamp's scope): keep the legacy published routing.
        if (program.model !== 'mentorship') openDesigner(base);
        else navigate(`${base}/matches`);
      } else {
        navigate(`${base}${isMasterclass ? '/settings?section=settings' : '/settings?section=members'}`);
      }
      return;
    }

    // orgLevelAdmin restructure: Continue lands straight on the per-type surface.
    //   Course / Guided Course / Hybrid → Program Designer (anything with lessons)
    //   Mentorship                      → Matches
    // Legacy (flag off): the old Program Settings sections.
    if (orgLevelAdmin) {
      if (program.model !== 'mentorship') openDesigner(base);
      else navigate(`${base}/matches`);
      return;
    }

    const suffix = isMasterclass ? '/settings?section=settings' : '/settings?section=members';
    navigate(`${base}${suffix}`);
  };

  const autoContinuedRef = React.useRef(false);
  React.useEffect(() => {
    if (!autoContinue || autoContinuedRef.current) return;
    autoContinuedRef.current = true;
    handleContinue();
  }, [autoContinue]); // eslint-disable-line react-hooks/exhaustive-deps

  // The card's parts: laid out as the tall tile on desktop, as one compact row on the phone.
  // Field stack — Figma: flex column, gap 8 between rows. NB: Figma reads "Publish By:" but
  // PRD §4 + Cian confirmed "Launch By:" is the canonical label. Kept mounted (no
  // unmountOnExit) — mounting the whole field stack on the first frame of the animation was the
  // other half of the stutter.
  const details = (
    <Collapse
      in={detailsOpen}
      timeout={280}
      easing={{ enter: 'cubic-bezier(0.4, 0, 0.2, 1)', exit: 'cubic-bezier(0.4, 0, 0.2, 1)' }}
      // Phone: once closed it leaves the card's flow, so the card's gap doesn't pad its bottom.
      sx={{ [MOBILE_SX]: { '&.MuiCollapse-hidden': { display: 'none' } } }}
    >
      {/* One column with its own rhythm — Collapse's wrapper is a plain div, so
      the card's gap doesn't reach inside it. pt separates the fields from the
      headline above so the open tile doesn't read as cramped. */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', pt: '12px' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <FieldRow
            label="Program"
            value={assessment ? 'Assessment' : program.model ? formatModelLabel(program.model) : 'Not set'}
          />
          <FieldRow
            label="Status"
            value={assessment ? assessment.status : formatStatusLabel(program.status)}
            valueColor={program.status === 'not-started' ? STATUS_NOT_STARTED_COLOR : undefined}
          />
          {/* Audience row — inline so AudienceTruncated can manage its own
        flex layout and keep the value on a single line with a +N chip. */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
            <Typography
              sx={{
                width: 80,
                flex: '0 0 auto',
                color: '#868686',
                fontFamily: 'Inter, sans-serif',
                fontSize: 12,
                fontWeight: 400,
                lineHeight: '14.4px'
              }}
            >
              Audience
            </Typography>
            {audienceLabels.length > 0 ? (
              <Tooltip
                title={
                  assessment
                    ? 'The roles this Assessment measures. Each person answers their own role’s capabilities.'
                    : roleLock
                    ? 'For these roles only. A program for roles takes its people from its roles.'
                    : ''
                }
                placement="bottom"
              >
                <Box
                  onClick={audienceEditable ? (e) => setAudienceAnchor(e.currentTarget) : undefined}
                  sx={{
                    cursor: audienceEditable ? 'pointer' : 'default',
                    display: 'inline-flex',
                    minWidth: 0,
                    borderRadius: '4px',
                    '&:hover': audienceEditable ? { opacity: 0.8 } : {}
                  }}
                >
                  {isMobileApp ? (
                    // Phone: every label, wrapping; the +N list is a hover tooltip, out of a finger's reach.
                    <Typography
                      sx={{
                        color: '#B6B6B6',
                        fontFamily: 'Inter, sans-serif',
                        fontSize: 12,
                        fontWeight: 400,
                        lineHeight: '16px',
                        overflowWrap: 'break-word'
                      }}
                    >
                      {audienceLabels.join(', ')}
                    </Typography>
                  ) : (
                    <AudienceTruncated labels={audienceLabels} />
                  )}
                </Box>
              </Tooltip>
            ) : audienceEditable ? (
              <AddFieldCTA onClick={(e) => setAudienceAnchor(e.currentTarget)}>Add Audience</AddFieldCTA>
            ) : (
              <Typography
                sx={{ color: '#B6B6B6', fontFamily: 'Inter, sans-serif', fontSize: 12, lineHeight: '14.4px' }}
              >
                Not set
              </Typography>
            )}
          </Box>
          {assessment && <FieldRow label={assessment.startLabel} value={assessment.startDate} />}
          <FieldRow
            label="Publish by"
            value={(() => {
              const canSetLaunch =
                program.status === 'design' || program.status === 'not-started' || program.status === 'published';
              if (!canSetLaunch) return 'Not set';
              if (!editable) return program.launchDate !== null ? formatCardDate(program.launchDate) : 'Not set';
              if (program.launchDate != null) {
                return (
                  <Box
                    component="span"
                    onClick={(e) => setLaunchByAnchor(e.currentTarget)}
                    sx={{ cursor: 'pointer', color: 'inherit', '&:hover': { textDecoration: 'underline' } }}
                  >
                    {formatCardDate(program.launchDate)}
                  </Box>
                );
              }
              return <AddFieldCTA onClick={(e) => setLaunchByAnchor(e.currentTarget)}>Add Publish By Date</AddFieldCTA>;
            })()}
            valueColor={launchByValueColor}
          />
          <DueByRows dueDate={program.programDueDate} existingColor={urgent ? URGENT_RED : undefined} />
        </Box>
        {/* Continue lives in the footer, so it stays put and every tile is the
      same size open. No progress bar here — the dial/pipeline above carries
      state. */}
      </Box>
    </Collapse>
  );
  // Owner (avatar + name OR Add Owner CTA), and the urgent flag.
  const ownerGroup = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
      {program.owner ? (
        <Box
          onClick={canEditOwner ? (e) => setOwnerAnchor(e.currentTarget) : undefined}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            cursor: canEditOwner ? 'pointer' : 'default',
            '&:hover': canEditOwner ? { opacity: 0.8 } : {}
          }}
        >
          {program.owner.headshot && (
            <Box
              component="img"
              src={program.owner.headshot}
              alt={program.owner.name}
              sx={{
                width: 16,
                height: 16,
                borderRadius: '9999px',
                objectFit: 'cover',
                flex: '0 0 auto',
                bgcolor: '#2A2A2A'
              }}
            />
          )}
          <Typography
            sx={{
              color: '#B6B6B6',
              fontFamily: 'Inter, sans-serif',
              fontSize: 12,
              fontWeight: 400,
              lineHeight: '14.4px'
            }}
          >
            {program.owner.name}
          </Typography>
        </Box>
      ) : canEditOwner ? (
        <AddFieldCTA onClick={(e) => setOwnerAnchor(e.currentTarget)}>Add Owner</AddFieldCTA>
      ) : (
        <Typography
          sx={{
            color: '#B6B6B6',
            fontFamily: 'Inter, sans-serif',
            fontSize: 12,
            fontWeight: 400,
            lineHeight: '14.4px'
          }}
        >
          Not set
        </Typography>
      )}
      {urgent && <UrgentFlag title={urgentTooltip} />}
    </Box>
  );
  // Open and the expander.
  const actions = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
      {/* Continue resumes a build — visible without opening the tile. A live
          program has nothing to resume, but the card is still the admin's
          only way into it from the dashboard, so it keeps the CTA as
          "Open" (designer / matches / settings via the same routing).
          Not offered read-only: every destination is an editor. */}
      {assessment
        ? assessment.onOpen && <ContinueLink label="Open" onClick={assessment.onOpen} loading={false} />
        : editable && (
            <ContinueLink label="Open" onClick={handleContinue} loading={isContinuing} />
          )}
      {/* Expander — reveals model, status, audience and launch date. */}
      <Tooltip title={detailsOpen ? 'Hide details' : 'Show details'}>
        <IconButton
          onClick={toggleDetails}
          aria-label={detailsOpen ? `Hide details for ${program.name}` : `Show details for ${program.name}`}
          sx={{ width: 22, height: 22, p: 0, color: '#F79420', [MOBILE_SX]: { width: 38, height: 38, mr: '-8px' } }}
        >
          {detailsOpen ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
        </IconButton>
      </Tooltip>
    </Box>
  );

  return (
    // Card wrapper — Figma: outer flex column, gap 12 between name pill
    // section and field stack.
    <Box
      sx={{
        // Lift + soft shadow: a lighter fill (clearly above the near-black page
        // now that the column fill is gone), a brighter border and a drop shadow
        // so each tile reads as a distinct, raised object.
        bgcolor: '#1E1E1E',
        border: `1px solid ${urgent ? URGENT_RED : '#555555'}`,
        borderRadius: '6px',
        position: 'relative',
        // Base lift shadow; when highlighted, add the brandy ring on top (inset
        // so it follows the rounded corners and never shifts layout).
        boxShadow: highlighted
          ? 'inset 0 0 0 2px #F79420, 0 6px 16px -8px rgba(0,0,0,0.85)'
          : '0 6px 16px -8px rgba(0,0,0,0.85)',
        p: '10px',
        display: 'flex',
        flexDirection: 'column',
        // Even 10px rhythm throughout rather than space-between, which pushed
        // the owner row away from the divider by whatever slack was left over.
        // `flex: 1` fills the collapsed card's fixed height; expanding lets the
        // card grow down instead of squeezing the details in.
        justifyContent: 'flex-start',
        gap: '10px',
        flex: 1,
        boxSizing: 'border-box',
        [MOBILE_SX]: { p: '12px' }
      }}
    >
      {/* Name pill — fills the card width. The old 260px minimum and the gap
          reserved for a floating padlock both came from the full-width
          dashboard; in the narrow split columns they forced long names to
          truncate early. The pill now spans the card and the padlock sits
          inside it, so the name gets every pixel that's actually free. */}
      <Box sx={{ display: 'flex', justifyContent: 'center', minWidth: 0 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            width: '100%',
            minWidth: 0,
            bgcolor: '#2A2A2A',
            border: '1px solid #444444',
            borderRadius: '4px',
            pt: '6px',
            pb: '6px',
            pl: '8px',
            pr: '8px',
            // Phone: the whole name, on as many lines as it takes, never cut.
            [MOBILE_SX]: {
              '& > .MuiTypography-root': {
                fontSize: 14,
                whiteSpace: 'normal',
                maxWidth: 'none',
                textAlign: 'center',
                overflowWrap: 'break-word'
              }
            }
          }}
        >
          {/* Type dot — hover names the program type (this replaced the legend
              that used to sit in the dashboard's top bar), click opens the
              category picker. */}
          <Tooltip
            title={
              assessment
                ? `${PROGRAM_TYPE_LABELS[program.programType]}. An Assessment is always Role Excellence.`
                : roleLock
                ? `${PROGRAM_TYPE_LABELS[program.programType]}. A program for roles keeps its category.`
                : PROGRAM_TYPE_LABELS[program.programType]
            }
            placement="bottom"
          >
            {/* The span takes the hover: the button is disabled when the type is fixed (a program for roles), and a disabled button gives none. */}
            <Box component="span" sx={{ display: 'inline-flex', flex: '0 0 auto' }}>
              <Box
                component="button"
                type="button"
                aria-label={`Program type: ${PROGRAM_TYPE_LABELS[program.programType]}${
                  typeEditable ? '. Change category' : ''
                }`}
                disabled={!typeEditable}
                onClick={typeEditable ? (e) => setTypeAnchor(e.currentTarget) : undefined}
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '9999px',
                  bgcolor: typeColor,
                  flex: '0 0 auto',
                  p: 0,
                  border: 'none',
                  cursor: typeEditable ? 'pointer' : 'default',
                  outline: 'none',
                  transition: 'transform 0.12s',
                  '&:hover': typeEditable ? { transform: 'scale(1.25)' } : {},
                  '&:focus-visible': { boxShadow: '0 0 0 2px #FDFDFD' },
                  // A finger-sized target around the dot on the phone.
                  [MOBILE_SX]: {
                    position: 'relative',
                    '&::after': { content: '""', position: 'absolute', inset: '-14px' }
                  }
                }}
              />
            </Box>
          </Tooltip>
          <ProgramNameText name={program.name} locked={Boolean(program.locked)} compact />
          {/* What kind of program it is: an Assessment measures, a Training teaches. Mentorship has no icon. */}
          {assessment ? (
            <ProgramKindIcon kind="assessment" size={14} />
          ) : (
            program.model !== 'mentorship' && <ProgramKindIcon kind="training" size={14} />
          )}
          {/* Padlock sits inline at the end of the pill — in the narrow columns
              a floating corner icon either overlapped the name or stole width
              from every card, locked or not. The name itself greys out too. */}
          {program.locked && (
            <Tooltip title={PROGRAM_LOCKED_TOOLTIP} placement="bottom">
              <LockOutlinedIcon
                sx={{
                  flexShrink: 0,
                  fontSize: 14,
                  color: '#F79420',
                  cursor: 'default'
                }}
              />
            </Tooltip>
          )}
        </Box>
      </Box>

      {isMobileApp ? (
        // Phone: under the name, one row: the dial (or the build stages, small), the owner, Open
        // and the expander, so a full-width card is half the tile's height and every action is a
        // tap. The details open beneath.
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
              {isLive ? (
                <CompletionDial rate={program.programCompletionRate} size={44} tooltip={assessment?.completion} />
              ) : (
                <StageStepper compact status={program.status} note={assessment?.stage} />
              )}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex' }}>{ownerGroup}</Box>
            {actions}
          </Box>
          {details}
        </>
      ) : (
        <>
          {/* Middle region — the headline sits in a FIXED-height block so a live
              tile (dial) and a not-live tile (build pipeline) are the same size both
              collapsed and open, and the headline barely shifts while the fields
              expand directly beneath it. The block is vertically centred in the
              collapsed card and always mounted, so only the fields animate. */}
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Box sx={{ height: 74, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {isLive ? (
                <CompletionDial rate={program.programCompletionRate} tooltip={assessment?.completion} />
              ) : (
                <StageStepper status={program.status} note={assessment?.stage} />
              )}
            </Box>

            {details}
          </Box>
          <Box sx={{ height: '1px', bgcolor: '#2A2A2A' }} />

          {/* Footer — Owner on left (avatar + name OR Add Owner CTA), Continue on right.
              When no owner is assigned the admin sees the "+ Add Owner" CTA
              (opens the OwnerPopover with the org's members). Non-admins see
              a dash. Creator is intentionally not displayed as a fallback —
              owner is the only person rendered here. */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              // Owner on the left; the right group holds the always-visible Continue
              // CTA for not-yet-live programs plus the expander.
              justifyContent: 'space-between',
              gap: '6px',
              flexShrink: 0
            }}
          >
            {ownerGroup}
            {actions}
          </Box>
        </>
      )}

      {/* Inline popover editors — opened by the +Add CTAs above. */}
      <AudiencePopover
        anchor={audienceAnchor}
        currentValue={audienceRules}
        catalog={audienceCatalog}
        onPreview={onAudiencePreview}
        onClose={() => setAudienceAnchor(null)}
        onSave={(next) => {
          onAudienceChange?.(next.length > 0 ? next : null);
          setAudienceAnchor(null);
        }}
      />
      <LaunchByPopover
        anchor={launchByAnchor}
        currentValue={program.launchDate}
        onClose={() => setLaunchByAnchor(null)}
        onSave={(next) => {
          onLaunchDateChange?.(next);
          setLaunchByAnchor(null);
        }}
      />
      <OwnerPopover
        anchor={ownerAnchor}
        existingMembers={existingMembers}
        currentOwnerId={program.owner?.id}
        onClose={() => setOwnerAnchor(null)}
        onSave={(next) => {
          onOwnerChange?.(next);
          setOwnerAnchor(null);
        }}
      />
      <ProgramTypePopover
        anchor={typeAnchor}
        currentValue={program.programType}
        onClose={() => setTypeAnchor(null)}
        onSave={(next) => {
          if (next !== program.programType) onProgramTypeChange?.(next);
          setTypeAnchor(null);
        }}
      />
    </Box>
  );
};

// Category (programType) picker — anchored to the colored dot on the program
// name pill. The dashboard no longer carries a legend, so these labels are the
// only place the type is named: the dot's tooltip reads from the same map.
// Selection persists immediately via PATCH /programs/{id}.
export const PROGRAM_TYPE_LABELS: Record<ProgramTypeKey, string> = {
  'role-excellence': 'Role Excellence',
  development: 'Development',
  standards: 'Standards'
};

const PROGRAM_TYPE_OPTIONS: Array<{ key: ProgramTypeKey; label: string }> = (
  Object.keys(PROGRAM_TYPE_LABELS) as ProgramTypeKey[]
).map((key) => ({ key, label: PROGRAM_TYPE_LABELS[key] }));

export const ProgramTypePopover: React.FC<{
  anchor: HTMLElement | null;
  currentValue: ProgramTypeKey;
  onClose: () => void;
  onSave: (next: ProgramTypeKey) => void;
}> = ({ anchor, currentValue, onClose, onSave }) => (
  <Popover
    open={Boolean(anchor)}
    anchorEl={anchor}
    onClose={onClose}
    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
    PaperProps={{
      sx: {
        mt: 1,
        bgcolor: '#1C1C1C',
        border: '1px solid #2A2A2A',
        borderRadius: '6px',
        width: 200
      }
    }}
  >
    {PROGRAM_TYPE_OPTIONS.map((opt) => {
      const selected = opt.key === currentValue;
      return (
        <Box
          key={opt.key}
          onClick={() => onSave(opt.key)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            px: '12px',
            py: '10px',
            cursor: 'pointer',
            transition: 'background-color 0.12s',
            '&:hover': { bgcolor: '#262626' }
          }}
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '9999px',
              bgcolor: PROGRAM_TYPE_COLORS[opt.key],
              flex: '0 0 auto'
            }}
          />
          <Typography
            sx={{
              flex: 1,
              color: selected ? '#FDFDFD' : '#B6B6B6',
              fontFamily: 'Inter, sans-serif',
              fontSize: 14,
              fontWeight: selected ? 500 : 400,
              lineHeight: '16.8px'
            }}
          >
            {opt.label}
          </Typography>
          {selected && <CheckIcon sx={{ fontSize: 14, color: '#F79420' }} />}
        </Box>
      );
    })}
  </Popover>
);

// The card's outline CTA. No icon: buttons carry text only. While its own
// navigation is in flight it reads "Opening..." instead of showing a spinner.
const ContinueLink: React.FC<{
  onClick: () => void;
  loading?: boolean;
  // "Continue" resumes a build; a live program has nothing to resume, so its
  // CTA reads "Open" (designer / matches / settings, same routing).
  label?: string;
}> = ({ onClick, loading, label = 'Open' }) => (
  <Box
    role="button"
    aria-busy={loading || undefined}
    onClick={loading ? undefined : onClick}
    sx={{
      // Outline button: a bordered, padded target so the whole shape is
      // clickable and clearly reads as the card's CTA (the old gradient-text
      // link was hard to see and only clickable dead-on the text).
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      flex: '0 0 auto',
      px: '11px',
      py: '5px',
      border: '1px solid #F79420',
      borderRadius: '6px',
      cursor: loading ? 'progress' : 'pointer',
      transition: 'background-color 0.15s',
      '&:hover': loading ? undefined : { backgroundColor: 'rgba(247, 148, 32, 0.12)' },
      // A finger-sized target on the phone.
      [MOBILE_SX]: { minHeight: 40, boxSizing: 'border-box', px: '16px', py: '10px' }
    }}
  >
    <Typography
      sx={{
        color: '#F79420',
        fontFamily: 'Inter, sans-serif',
        fontSize: 12,
        fontWeight: 600,
        lineHeight: '14.4px'
      }}
    >
      {loading ? 'Opening...' : label}
    </Typography>
  </Box>
);

// Audience value — always single line. Fills available width with
// comma-separated labels, then shows a "+N" chip IMMEDIATELY to the right
// of the last visible label (not at the far right of the card).
//
// Measurement uses an off-screen ruler span so flex-expansion of the
// in-tree element never pollutes the width calculation.
const AUDIENCE_CHIP_W = 28; // px reserved for the "+N" chip + gap
const AUDIENCE_FONT = '400 12px/14.4px Inter, sans-serif';

const AudienceTruncated: React.FC<{ labels: string[] }> = ({ labels }) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = React.useState(labels.length);

  React.useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container || labels.length === 0) return;

    const raf = requestAnimationFrame(() => {
      if (!containerRef.current) return;
      const availableW = containerRef.current.clientWidth;
      if (availableW === 0) return; // not yet laid out

      // Off-screen ruler — measures natural (unwrapped) text width without
      // any flex-expansion interference from the in-tree element.
      const ruler = document.createElement('span');
      ruler.style.cssText =
        'position:absolute;top:-9999px;left:-9999px;visibility:hidden;' +
        'white-space:nowrap;pointer-events:none;font:' +
        AUDIENCE_FONT +
        ';';
      document.body.appendChild(ruler);

      try {
        // 1. Does the full text fit?
        ruler.textContent = labels.join(', ');
        if (ruler.offsetWidth <= availableW) {
          setVisibleCount(labels.length);
          return;
        }

        // 2. Overflow — binary-search for how many whole labels fit when
        //    the chip is also visible (reserve AUDIENCE_CHIP_W px for it).
        const textW = availableW - AUDIENCE_CHIP_W;
        let lo = 0;
        let hi = labels.length - 1; // keep at least 1 in the chip
        while (lo < hi) {
          const mid = Math.ceil((lo + hi) / 2);
          ruler.textContent = labels.slice(0, mid).join(', ');
          if (ruler.offsetWidth <= textW) lo = mid;
          else hi = mid - 1;
        }
        // lo === 0 means even a single label is too long — show it anyway
        // and let CSS ellipsis trim it; still count the rest in the chip.
        setVisibleCount(Math.max(lo, 1));
      } finally {
        document.body.removeChild(ruler);
      }
    });

    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [labels.join('|')]);

  const overflow = labels.length - visibleCount;
  const visibleText = labels.slice(0, visibleCount).join(', ');

  // Bulleted full list in the tooltip.
  const tooltipContent = (
    <Box component="ul" sx={{ m: 0, pl: '14px', listStyleType: 'disc' }}>
      {labels.map((l, i) => (
        <Box
          key={i}
          component="li"
          sx={{ fontFamily: 'Inter, sans-serif', fontSize: 12, lineHeight: '18px', color: '#FDFDFD' }}
        >
          {l}
        </Box>
      ))}
    </Box>
  );

  return (
    // Container takes the remaining row width; items are packed left so the
    // chip sits immediately after the last visible label.
    <Box
      ref={containerRef}
      sx={{
        display: 'flex',
        alignItems: 'center',
        flex: '1 1 0',
        minWidth: 0,
        overflow: 'hidden',
        gap: '4px'
      }}
    >
      {/* flex: '0 1 auto' — shrinks when needed but does NOT expand, so
          the chip lands right-of-text rather than at the far-right edge. */}
      <Box
        component="span"
        sx={{
          flex: '0 1 auto',
          minWidth: 0,
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
          color: '#B6B6B6',
          fontFamily: 'Inter, sans-serif',
          fontSize: 12,
          fontWeight: 400,
          lineHeight: '14.4px'
        }}
      >
        {visibleText}
      </Box>
      {overflow > 0 && (
        <Tooltip title={tooltipContent} placement="bottom">
          <Box
            sx={{
              flex: '0 0 auto',
              bgcolor: '#2A2A2A',
              border: '1px solid #444444',
              borderRadius: '4px',
              px: '4px',
              py: '1px',
              cursor: 'default'
            }}
          >
            <Typography
              sx={{
                color: '#868686',
                fontFamily: 'Inter, sans-serif',
                fontSize: 11,
                fontWeight: 500,
                lineHeight: 1,
                whiteSpace: 'nowrap'
              }}
            >
              +{overflow}
            </Typography>
          </Box>
        </Tooltip>
      )}
    </Box>
  );
};

// Field row — Figma: flex row, gap 10, fixed-width 91px label + flex-1 value.
// Both pieces share Inter / weight 400; only colour differs.
// Font size bumped from Figma's spec'd 12 → 14 per Cian's adjustment after
// visual review (12 was too tight relative to the 15/600 program-name pill).
const FieldRow: React.FC<{
  label: string;
  value: React.ReactNode;
  valueColor?: string;
}> = ({ label, value, valueColor }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
    <Typography
      sx={{
        width: 80,
        flex: '0 0 auto',
        color: '#868686', // Text/text-tertiary
        fontFamily: 'Inter, sans-serif',
        fontSize: 12,
        fontWeight: 400,
        lineHeight: '14.4px'
      }}
    >
      {label}
    </Typography>
    <Typography
      component="div"
      sx={{
        flex: '1 1 0',
        color: valueColor ?? '#B6B6B6', // default Text/text-secondary; override for amber statuses
        fontFamily: 'Inter, sans-serif',
        fontSize: 12,
        fontWeight: 400,
        lineHeight: '14.4px',
        wordBreak: 'break-word'
      }}
    >
      {value}
    </Typography>
  </Box>
);

// "30 days after joining" — a days-after-signup rule with no shared date.
const formatDaysAfterJoining = (days: number | null): string =>
  days === null ? 'Not set' : `${days} ${days === 1 ? 'day' : 'days'} after joining`;

/**
 * Due By, a sub heading per learner group. Read-only: the dates are set from the
 * alarm clock in the Program Designer. Existing learners share one date; new
 * starters are usually given a number of days from when they join.
 */
const DueByRows: React.FC<{ dueDate: ProgramDueDate | null; existingColor?: string }> = ({
  dueDate,
  existingColor
}) => {
  const existingDue = existingLearnersDueBy(dueDate);
  const existingRule = dueDate?.existingMembers;
  const existing =
    existingDue !== null
      ? formatCardDate(existingDue)
      : existingRule?.type === 'daysAfterSignup'
      ? formatDaysAfterJoining(existingRule.value)
      : 'Not set';
  const newRule = dueDate?.newMembers;
  const newStarters =
    newRule?.type === 'calendarDate' && newRule.value
      ? formatCardDate(newRule.value)
      : newRule?.type === 'daysAfterSignup'
      ? formatDaysAfterJoining(newRule.value)
      : 'Not set';
  const textSx = { fontFamily: 'Inter, sans-serif', fontSize: 12, fontWeight: 400, lineHeight: '14.4px' };
  // Each learner group is a sub heading with its date beneath, in the value
  // column — "Due By:" keeps the label column like every other field.
  const group = (heading: string, value: string, color?: string) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
      <Typography
        sx={{
          fontFamily: 'Inter, sans-serif',
          fontSize: 11,
          fontWeight: 700,
          lineHeight: '13px',
          color: '#868686'
        }}
      >
        {heading}
      </Typography>
      <Typography sx={{ ...textSx, color: color ?? '#B6B6B6' }}>{value}</Typography>
    </Box>
  );
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
      <Typography sx={{ ...textSx, width: 80, flex: '0 0 auto', color: '#868686' }}>Due by</Typography>
      <Box sx={{ flex: '1 1 0', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {group('Existing learners', existing, existingDue !== null ? existingColor : undefined)}
        {group('New starters', newStarters)}
      </Box>
    </Box>
  );
};

// The learner card's urgent flag, on the admin card.
const UrgentFlag: React.FC<{ title: string }> = ({ title }) => (
  <Tooltip title={title} placement="bottom">
    <Box
      component="span"
      sx={{
        width: 15,
        height: 15,
        flexShrink: 0,
        borderRadius: '50%',
        bgcolor: URGENT_RED,
        color: '#FDFDFD',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'Inter, sans-serif',
        fontSize: 11,
        fontWeight: 800,
        lineHeight: 1,
        cursor: 'default'
      }}
    >
      !
    </Box>
  </Tooltip>
);

// Inline "+ Add X" CTA shown on incomplete-field values.
// Figma: Inter 12 / 700 underline #B6B6B6 — font size bumped to 14 for
// session consistency with the rest of the card body text.
// onClick receives the click event so callers can anchor a Popover to it.
const AddFieldCTA: React.FC<{
  onClick: (e: React.MouseEvent<HTMLElement>) => void;
  children: React.ReactNode;
}> = ({ onClick, children }) => (
  <Box
    component="span"
    role="button"
    onClick={onClick}
    sx={{
      color: '#B6B6B6',
      fontFamily: 'Inter, sans-serif',
      fontSize: 12,
      fontWeight: 700,
      lineHeight: '14.4px',
      textDecoration: 'underline',
      cursor: 'pointer',
      '&:hover': { color: '#FDFDFD' }
    }}
  >
    {children}
  </Box>
);

// ─── Inline popover editors ─────────────────────────────────────────────
//
// Each editor anchors to the +Add CTA that triggered it. Styling matches
// the drawer's equivalent fields so the experience is consistent. All
// committed values flow back to the dashboard via the card's onChange
// props; dashboard updates `programs` state and persists to localStorage.

// Audience — one section per org attribute list, each with its own Any/All
// toggle and checkboxes. Every list the org has configured is always shown
// (there is no "+ Add list" step); a list with nothing ticked simply doesn't
// participate in matching and is never sent to the BE.
//
// Matching semantics the UI has to convey: AND across lists, `matchType`
// within a list. "Any" = holds at least one ticked value; "All" = holds every
// ticked value.
//
// Save runs a dry-run preview first; if the change would remove members, the
// admin gets an impact confirmation before anything is committed.
const AudienceMatchToggle: React.FC<{
  value: AudienceMatchType;
  disabled: boolean;
  onChange: (next: AudienceMatchType) => void;
}> = ({ value, disabled, onChange }) => (
  <Box
    sx={{
      display: 'inline-flex',
      border: '1px solid #2A2A2A',
      borderRadius: '4px',
      overflow: 'hidden',
      opacity: disabled ? 0.4 : 1
    }}
  >
    {(['any', 'all'] as AudienceMatchType[]).map((mode) => {
      const active = value === mode;
      return (
        <Box
          key={mode}
          component="button"
          type="button"
          disabled={disabled}
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            if (!disabled) onChange(mode);
          }}
          sx={{
            px: '8px',
            py: '2px',
            border: 'none',
            bgcolor: active ? '#F79420' : 'transparent',
            color: active ? '#FFFFFF' : '#868686',
            fontFamily: 'Inter, sans-serif',
            fontSize: 10,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            cursor: disabled ? 'default' : 'pointer',
            transition: 'all 0.12s',
            '&:hover': disabled ? {} : { bgcolor: active ? '#F79420' : '#262626' }
          }}
        >
          {mode}
        </Box>
      );
    })}
  </Box>
);

export const AudiencePopover: React.FC<{
  anchor: HTMLElement | null;
  currentValue: AudienceRule[];
  catalog: MemberAttributeGroupCatalog[];
  onClose: () => void;
  onSave: (next: AudienceRule[]) => void;
  onPreview?: (next: AudienceRule[]) => Promise<{ toAdd: number; toRemove: number } | null>;
}> = ({ anchor, currentValue, catalog, onClose, onSave, onPreview }) => {
  // Draft is keyed by listId so a list the admin hasn't touched keeps its
  // stored matchType, and an untouched empty list stays absent.
  const [draft, setDraft] = useState<Record<string, AudienceRule>>({});
  const [pending, setPending] = useState<{ toAdd: number; toRemove: number } | null>(null);
  const [checking, setChecking] = useState(false);

  React.useEffect(() => {
    if (!anchor) return;
    const next: Record<string, AudienceRule> = {};
    for (const rule of currentValue) next[rule.listId] = { ...rule, valueIds: [...rule.valueIds] };
    setDraft(next);
    setPending(null);
    setChecking(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchor]);

  const ruleFor = (list: MemberAttributeGroupCatalog): AudienceRule =>
    draft[list._id] ?? {
      listId: list._id,
      listName: list.name,
      matchType: 'any',
      valueIds: []
    };

  const toggleValue = (list: MemberAttributeGroupCatalog, valueId: string) =>
    setDraft((prev) => {
      const rule = prev[list._id] ?? {
        listId: list._id,
        listName: list.name,
        matchType: 'any' as AudienceMatchType,
        valueIds: []
      };
      const valueIds = rule.valueIds.includes(valueId)
        ? rule.valueIds.filter((v) => v !== valueId)
        : [...rule.valueIds, valueId];
      return { ...prev, [list._id]: { ...rule, listName: list.name, valueIds } };
    });

  const setMatchType = (list: MemberAttributeGroupCatalog, matchType: AudienceMatchType) =>
    setDraft((prev) => {
      const rule = prev[list._id];
      if (!rule) return prev;
      return { ...prev, [list._id]: { ...rule, matchType } };
    });

  const draftRules = participatingAudienceRules(Object.values(draft));

  const commit = () => {
    onSave(draftRules);
    setPending(null);
  };

  const handleSave = async () => {
    if (!onPreview) {
      commit();
      return;
    }
    setChecking(true);
    const impact = await onPreview(draftRules);
    setChecking(false);
    // Only interrupt when the change actually costs someone their place.
    if (impact && impact.toRemove > 0) {
      setPending(impact);
      return;
    }
    commit();
  };

  return (
    <Popover
      open={Boolean(anchor)}
      anchorEl={anchor}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      PaperProps={{
        sx: {
          mt: 1,
          bgcolor: '#1C1C1C',
          border: '1px solid #2A2A2A',
          borderRadius: '6px',
          width: 260,
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      {/* Header */}
      <Typography
        sx={{
          color: '#FDFDFD',
          fontFamily: 'Inter, sans-serif',
          fontSize: 13,
          fontWeight: 500,
          px: '12px',
          pt: '12px',
          pb: '8px'
        }}
      >
        Select audience
      </Typography>

      {/* One section per org list — all lists always visible */}
      <Box sx={{ display: 'flex', flexDirection: 'column', maxHeight: 300, overflowY: 'auto' }}>
        {catalog.length === 0 && (
          <Typography sx={{ color: '#868686', fontFamily: 'Inter, sans-serif', fontSize: 13, px: '12px', py: '8px' }}>
            Add attributes in Organization Settings to choose an audience here.
          </Typography>
        )}
        {catalog.map((list, listIdx) => {
          const rule = ruleFor(list);
          const hasSelection = rule.valueIds.length > 0;
          return (
            <Box key={list._id || listIdx}>
              {/* List header + Any/All. The toggle is disabled until something
                  is ticked — a match type with no values has no meaning. */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '8px',
                  px: '12px',
                  pt: listIdx === 0 ? '4px' : '12px',
                  pb: '4px'
                }}
              >
                <Typography
                  sx={{
                    color: hasSelection ? '#868686' : '#555555',
                    fontFamily: 'Inter, sans-serif',
                    fontSize: 10,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {list.name}
                </Typography>
                <AudienceMatchToggle
                  value={rule.matchType}
                  disabled={!hasSelection}
                  onChange={(next) => setMatchType(list, next)}
                />
              </Box>

              {list.values.map((option) => {
                const selected = rule.valueIds.includes(option._id);
                return (
                  <Box
                    key={option._id}
                    onClick={() => toggleValue(list, option._id)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      px: '12px',
                      py: '8px',
                      cursor: 'pointer',
                      transition: 'background-color 0.12s',
                      '&:hover': { bgcolor: '#262626' }
                    }}
                  >
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        borderRadius: '3px',
                        border: `1.5px solid ${selected ? '#F79420' : '#444444'}`,
                        bgcolor: selected ? '#F79420' : 'transparent',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flex: '0 0 auto',
                        transition: 'all 0.12s'
                      }}
                    >
                      {selected && <CheckIcon sx={{ fontSize: 12, color: '#FFFFFF' }} />}
                    </Box>
                    <Typography
                      sx={{
                        color: selected ? '#FDFDFD' : '#B6B6B6',
                        fontFamily: 'Inter, sans-serif',
                        fontSize: 14,
                        fontWeight: 400,
                        lineHeight: '16.8px'
                      }}
                    >
                      {option.value}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          );
        })}
      </Box>

      {/* Impact confirmation — only shown when the pending change would
          actually remove people. Manually-invited members, mentors and admins
          are never counted here; the BE only removes auto-matched members. */}
      {pending ? (
        <Box sx={{ p: '12px', borderTop: '1px solid #2A2A2A' }}>
          <Typography
            sx={{
              color: '#FDFDFD',
              fontFamily: 'Inter, sans-serif',
              fontSize: 13,
              fontWeight: 600,
              mb: '4px'
            }}
          >
            This will remove {pending.toRemove} {pending.toRemove === 1 ? 'person' : 'people'}
          </Typography>
          <Typography
            sx={{
              color: '#868686',
              fontFamily: 'Inter, sans-serif',
              fontSize: 12,
              lineHeight: '16px',
              mb: '10px'
            }}
          >
            {pending.toRemove === 1 ? 'They no longer match' : 'They no longer match'} this audience and will lose
            access to the program.
            {pending.toAdd > 0
              ? ` ${pending.toAdd} new ${pending.toAdd === 1 ? 'person' : 'people'} will be added.`
              : ''}{' '}
            People enrolled by an Admin are not affected.
          </Typography>
          <Box sx={{ display: 'flex', gap: '8px' }}>
            <DosenButton tone="basic" sizeVariant="sm" onClick={() => setPending(null)} sx={{ flex: 1 }}>
              Cancel
            </DosenButton>
            <DosenButton tone="cta" sizeVariant="sm" onClick={commit} sx={{ flex: 1 }}>
              Save Audience
            </DosenButton>
          </Box>
        </Box>
      ) : (
        <Box sx={{ p: '12px', pt: '8px', borderTop: '1px solid #2A2A2A' }}>
          <DosenButton
            tone="cta"
            sizeVariant="sm"
            loading={checking}
            loadingText="Checking Impact..."
            onClick={handleSave}
            sx={{ width: '100%' }}
          >
            Save
          </DosenButton>
        </Box>
      )}
    </Popover>
  );
};

// Launch By — DesktopDatePicker styled like the drawer's popup.
export const LaunchByPopover: React.FC<{
  anchor: HTMLElement | null;
  currentValue: number | null;
  onClose: () => void;
  onSave: (next: number | null) => void;
}> = ({ anchor, currentValue, onClose, onSave }) => {
  const [draft, setDraft] = useState<Date | null>(currentValue ? new Date(currentValue * 1000) : null);
  React.useEffect(() => {
    if (anchor) setDraft(currentValue ? new Date(currentValue * 1000) : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchor]);
  return (
    <Popover
      open={Boolean(anchor)}
      anchorEl={anchor}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      PaperProps={{
        sx: {
          mt: 1,
          bgcolor: '#1C1C1C',
          border: '1px solid #2A2A2A',
          borderRadius: '6px',
          p: '12px',
          width: 260,
          display: 'flex',
          flexDirection: 'column',
          gap: '8px'
        }
      }}
    >
      <Typography
        sx={{
          color: '#FDFDFD',
          fontFamily: 'Inter, sans-serif',
          fontSize: 13,
          fontWeight: 500
        }}
      >
        Publish by
      </Typography>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <DesktopDatePicker
          value={draft}
          onChange={(d: Date | null) => setDraft(d)}
          format="MMM d, yyyy"
          slots={{
            openPickerIcon: () => <CalendarTodayOutlinedIcon sx={{ fontSize: 16, color: '#868686' }} />
          }}
          slotProps={{
            textField: {
              size: 'small',
              fullWidth: true,
              placeholder: 'MMM D, YYYY',
              sx: {
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'rgba(0, 0, 0, 0.70)',
                  borderRadius: '6px',
                  color: '#FDFDFD',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: 14,
                  '& fieldset': { borderColor: '#444444' }
                },
                '& .MuiOutlinedInput-input': { py: '8px', px: '12px' }
              },
              InputProps: {
                startAdornment: (
                  <InputAdornment position="start">
                    <CalendarTodayOutlinedIcon sx={{ color: '#868686', fontSize: 16 }} />
                  </InputAdornment>
                )
              }
            },
            popper: {
              sx: {
                '& .MuiPaper-root': {
                  bgcolor: '#151515',
                  border: '1px solid #444444',
                  borderRadius: '4px',
                  boxShadow: '0px 0px 15px rgba(255, 255, 255, 0.12)',
                  p: '8px'
                },
                '& .MuiPickersCalendarHeader-label': {
                  color: '#FDFDFD',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: 16,
                  fontWeight: 400
                },
                '& .MuiPickersArrowSwitcher-button': {
                  width: 28,
                  height: 28,
                  bgcolor: '#565656',
                  borderRadius: '4px',
                  color: '#FDFDFD',
                  '& .MuiSvgIcon-root': { fontSize: 16, color: '#FDFDFD' }
                },
                '& .MuiDayCalendar-weekDayLabel': {
                  color: '#B6B6B6',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: 12,
                  fontWeight: 500
                },
                '& .MuiPickersDay-root': {
                  color: '#FDFDFD',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: 12,
                  fontWeight: 500,
                  borderRadius: '999px',
                  '&:hover': { bgcolor: '#2A2A2A' },
                  '&.Mui-selected, &.Mui-selected:hover, &.Mui-selected:focus': {
                    background: 'linear-gradient(180deg, #F79420 0%, #F15A26 100%)',
                    color: '#FDFDFD'
                  },
                  '&.MuiPickersDay-dayOutsideMonth': { color: '#868686' }
                }
              }
            }
          }}
        />
      </LocalizationProvider>
      <Box
        component="button"
        type="button"
        onClick={() => onSave(draft ? Math.floor(draft.getTime() / 1000) : null)}
        sx={{
          mt: 0.5,
          py: '8px',
          background: 'linear-gradient(180deg, #F79420 0%, #F15A26 100%)',
          border: 'none',
          borderRadius: '6px',
          color: '#FDFDFD',
          fontFamily: 'Inter, sans-serif',
          fontSize: 13,
          fontWeight: 600,
          cursor: 'pointer',
          '&:hover': { opacity: 0.92 }
        }}
      >
        Save
      </Box>
    </Popover>
  );
};

// Owner — existing-member dropdown. For inline edits we only support
// picking an existing org member; inviting a brand-new owner stays in
// the Add New Program drawer (where the full invite form lives).
export const OwnerPopover: React.FC<{
  anchor: HTMLElement | null;
  existingMembers: ProgramOwner[];
  currentOwnerId?: string;
  onClose: () => void;
  onSave: (next: ProgramOwner | null) => void;
}> = ({ anchor, existingMembers, currentOwnerId, onClose, onSave }) => {
  const [selectedId, setSelectedId] = useState<string>(currentOwnerId ?? '');
  // Pre-select the program's current owner each time the popover opens so the
  // admin edits from the existing value instead of a blank select.
  React.useEffect(() => {
    if (anchor) setSelectedId(currentOwnerId ?? '');
  }, [anchor, currentOwnerId]);
  return (
    <Popover
      open={Boolean(anchor)}
      anchorEl={anchor}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      PaperProps={{
        sx: {
          mt: 1,
          bgcolor: '#1C1C1C',
          border: '1px solid #2A2A2A',
          borderRadius: '6px',
          p: '12px',
          width: 260,
          display: 'flex',
          flexDirection: 'column',
          gap: '8px'
        }
      }}
    >
      <Typography
        sx={{
          color: '#FDFDFD',
          fontFamily: 'Inter, sans-serif',
          fontSize: 13,
          fontWeight: 500
        }}
      >
        Select Owner
      </Typography>
      <Select
        value={selectedId}
        onChange={(e) => setSelectedId(e.target.value)}
        displayEmpty
        IconComponent={KeyboardArrowDownIcon}
        renderValue={(val) => {
          if (!val)
            return (
              <Box component="span" sx={{ color: '#868686' }}>
                Select...
              </Box>
            );
          return existingMembers.find((m) => m.id === val)?.name ?? '';
        }}
        sx={{
          bgcolor: 'rgba(0, 0, 0, 0.70)',
          borderRadius: '6px',
          color: '#FDFDFD',
          fontFamily: 'Inter, sans-serif',
          fontSize: 14,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#444444' },
          '& .MuiSelect-select': { py: '8px', px: '12px' },
          '& .MuiSelect-icon': { color: '#868686', right: '12px' }
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              bgcolor: '#1C1C1C',
              border: '1px solid #2A2A2A',
              '& .MuiMenuItem-root': {
                color: '#B6B6B6',
                fontFamily: 'Inter, sans-serif',
                fontSize: 14
              }
            }
          }
        }}
      >
        {existingMembers.map((m) => (
          <MenuItem key={m.id} value={m.id}>
            {m.name}
          </MenuItem>
        ))}
      </Select>
      <Box
        component="button"
        type="button"
        onClick={() => {
          const m = existingMembers.find((x) => x.id === selectedId) ?? null;
          onSave(m);
        }}
        sx={{
          mt: 0.5,
          py: '8px',
          background: 'linear-gradient(180deg, #F79420 0%, #F15A26 100%)',
          border: 'none',
          borderRadius: '6px',
          color: '#FDFDFD',
          fontFamily: 'Inter, sans-serif',
          fontSize: 13,
          fontWeight: 600,
          cursor: 'pointer',
          '&:hover': { opacity: 0.92 }
        }}
      >
        Save
      </Box>
    </Popover>
  );
};

export default ProgramCard;
